import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { usePatients } from '../../api/hooks';
import { useSession } from '../../app/session';
import { ErrorState, Loading, TableEmpty } from '../../components/States';
import { StatusPill } from '../../components/Status';
import { useDebounced } from '../../components/useDebounced';
import { fmtDateFull } from '../../lib/dates';
import { patientName, sexAge } from '../../lib/patients';

export function PatientsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgParam, orgs } = useSession();
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim());
  const patients = usePatients(orgParam, q || undefined);
  const rows = [...(patients.data ?? [])].sort((a, b) => patientName(a).localeCompare(patientName(b), 'ru'));
  const many = orgs.length > 1;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('doctor.patients.title')}</h1>
          <p>{t('doctor.patients.sub', { count: rows.length })}</p>
        </div>
        <Link className="btn btn-primary" to="/new">
          {t('doctor.patients.register')}
        </Link>
      </div>
      <div className="toolbar">
        <input
          className="input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('doctor.patients.search')}
          aria-label={t('doctor.patients.search')}
        />
      </div>
      {patients.isLoading ? (
        <Loading />
      ) : patients.error && !patients.data ? (
        <ErrorState error={patients.error} onRetry={() => void patients.refetch()} />
      ) : !rows.length ? (
        <TableEmpty>{q ? t('doctor.patients.notFound') : t('doctor.patients.empty')}</TableEmpty>
      ) : (
        <div className="table-wrap stack">
          <table>
            <thead>
              <tr>
                <th>{t('doctor.patients.cols.patient')}</th>
                <th>{t('doctor.patients.cols.dob')}</th>
                <th>{t('doctor.patients.cols.cases')}</th>
                <th>{t('doctor.patients.cols.last')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const open = () => p.last_case && navigate(`/cases/${encodeURIComponent(p.last_case.id)}`);
                return (
                  <tr
                    key={p.id}
                    tabIndex={0}
                    onClick={open}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target === e.currentTarget) open();
                    }}
                  >
                    <td className="wide">
                      <div className="pname">{patientName(p)}</div>
                      <div className="case-id">
                        {sexAge(p)}
                        {many ? `, ${p.clinic.name}` : ''}
                      </div>
                    </td>
                    <td data-label={t('doctor.patients.cols.dob')}>{fmtDateFull(p.birth_date)}</td>
                    <td data-label={t('doctor.patients.cols.cases')}>{p.cases_count}</td>
                    <td data-label={t('doctor.patients.cols.last')}>
                      {p.last_case ? (
                        <StatusPill status={p.last_case.status} />
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="wide" style={{ textAlign: 'right' }}>
                      <Link
                        className="btn btn-sm"
                        to={`/new?patient=${encodeURIComponent(p.id)}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('nav.newCase')}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
